// src/pages/interfaces/InterfacesPage.tsx

import {
  Activity,
  ArrowUpDown,
  CheckCircle2,
  EthernetPort,
  Filter,
  Gauge,
  Loader2,
  Network,
  Plus,
  Power,
  RefreshCw,
  Search,
  Server,
  Settings,
  ShieldCheck,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import InterfaceFormModal, {
  FormType,
} from "../../components/interface/InterfaceFormModal";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useToast } from "../../components/ui/Toast";
import { fetchApp, fetchMikrotik } from "../../services/apiService";
import type { Interface, RouterInterfaces } from "../../types/interface";

type RouterId = string | number;

interface FlattenedInterface extends Interface {
  routerName: string;
  routerId: RouterId;
  routerIp?: string;
}

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const getBool = (value: unknown) => {
  if (typeof value === "boolean") return value;

  const text = String(value ?? "")
    .trim()
    .toLowerCase();

  return text === "true" || text === "yes" || text === "1";
};

const getInterfaceDisabled = (intf: Interface) =>
  getBool((intf as any).disabled);

const getInterfaceRunning = (intf: Interface) => getBool((intf as any).running);

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading network interfaces…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while router interface information is being prepared.
          </p>

          <div className="relative mt-6 flex justify-center gap-1.5">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-teal-500 delay-150" />
            <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-500 delay-300" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const StatusBadge: React.FC<{ intf: Interface }> = ({ intf }) => {
  const disabled = getInterfaceDisabled(intf);
  const running = getInterfaceRunning(intf);

  if (disabled) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
        <XCircle className="h-3 w-3" />
        Disabled
      </span>
    );
  }

  if (running) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <CheckCircle2 className="h-3 w-3" />
        Running
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-[11px] font-black text-amber-800">
      <Activity className="h-3 w-3" />
      Idle
    </span>
  );
};

const TypeBadge: React.FC<{ type?: string }> = ({ type }) => {
  const value = type || "ethernet";

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-100 px-2.5 py-1 text-[11px] font-black text-sky-800">
      <EthernetPort className="h-3 w-3" />
      {value}
    </span>
  );
};

const TableSkeleton: React.FC = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 7 }).map((_, index) => (
      <td key={index} className="px-5 py-4">
        <div
          className={`h-4 rounded-lg bg-slate-200 ${
            index === 0
              ? "w-40"
              : index === 4
                ? "w-28"
                : index === 6
                  ? "w-16"
                  : "w-24"
          }`}
        />
      </td>
    ))}
  </tr>
);

const MobileSkeleton: React.FC = () => (
  <div className="animate-pulse px-4 py-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-2">
        <div className="h-4 w-40 rounded bg-slate-200" />
        <div className="h-3 w-28 rounded bg-slate-200" />
      </div>

      <div className="h-6 w-20 rounded-full bg-slate-200" />
    </div>

    <div className="mt-4 grid grid-cols-2 gap-2">
      <div className="h-14 rounded-2xl bg-slate-200" />
      <div className="h-14 rounded-2xl bg-slate-200" />
    </div>
  </div>
);

const InterfacesPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [routerInterfaces, setRouterInterfaces] = useState<RouterInterfaces[]>(
    [],
  );
  const [loading, setLoading] = useState(false);

  const [ethernetLoading, setEthernetLoading] = useState(false);
  const [vlanLoading, setVlanLoading] = useState(false);
  const [ipLoading, setIpLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedRouterId, setSelectedRouterId] = useState<string>("all");

  const [ethernetForm, setEthernetForm] = useState({
    name: "",
    comment: "",
    mtu: "1500",
    arp: "enabled",
    auto_negotiation: "yes",
    speed: "",
  });

  const [vlanForm, setVlanForm] = useState({
    name: "",
    interface: "",
    vlan_id: "",
    comment: "",
    mtu: "1500",
  });

  const [ipForm, setIpForm] = useState({
    address: "",
    interface: "",
    network: "",
    comment: "",
  });

  const [activeForm, setActiveForm] = useState<FormType>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    routerId: RouterId;
    name: string;
    disabled: boolean;
  } | null>(null);

  const openForm = (type: FormType) => {
    setActiveForm(type);
    setIsModalOpen(true);
    setShowAddMenu(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setActiveForm(null);
  };

  const formatBytes = (bytes?: number) => {
    if (!bytes || bytes === 0) return "0 B";

    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);

    return `${parseFloat(value.toFixed(2))} ${sizes[i]}`;
  };

  const fetchInterfaces = async () => {
    try {
      setLoading(true);

      const data = await fetchApp<RouterInterfaces[]>("/interface/");
      const rows = Array.isArray(data) ? data : [];

      setRouterInterfaces(rows);

      const failed = rows.filter(
        (router: any) => router && router.ok === false,
      );

      if (failed.length > 0) {
        const list = failed
          .map(
            (router: any) =>
              `${router.router_name || `Router ${router.router_id}`}: ${
                router.error || "Failed"
              }`,
          )
          .slice(0, 3);

        const more = failed.length - list.length;

        showToast(
          `Loaded interfaces with ${failed.length} router error(s). ${list.join(
            " | ",
          )}${more > 0 ? ` (+${more} more)` : ""}`,
          "error",
        );
      }
    } catch (err: any) {
      console.error(err);
      showToast(err?.message || "Failed to load interfaces", "error");
      setRouterInterfaces([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterfaces();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allInterfaces = useMemo<FlattenedInterface[]>(() => {
    return routerInterfaces.flatMap((router: any) =>
      (router.interfaces || []).map((intf: any) => ({
        ...intf,
        routerName: router.router_name,
        routerId: router.router_id,
        routerIp: router.router_ip,
      })),
    );
  }, [routerInterfaces]);

  const filteredInterfaces = useMemo(() => {
    const term = search.toLowerCase().trim();

    return allInterfaces.filter((item) => {
      if (
        selectedRouterId !== "all" &&
        String(item.routerId) !== selectedRouterId
      ) {
        return false;
      }

      if (!term) return true;

      return (
        String(item.name || "")
          .toLowerCase()
          .includes(term) ||
        String(item.routerName || "")
          .toLowerCase()
          .includes(term) ||
        String(item.comment || "")
          .toLowerCase()
          .includes(term) ||
        String(item.type || "")
          .toLowerCase()
          .includes(term)
      );
    });
  }, [allInterfaces, search, selectedRouterId]);

  const stats = useMemo(() => {
    const totalRouters = routerInterfaces.length;
    const total = filteredInterfaces.length;
    const running = filteredInterfaces.filter(
      (item) => !getInterfaceDisabled(item) && getInterfaceRunning(item),
    ).length;
    const disabled = filteredInterfaces.filter(getInterfaceDisabled).length;
    const idle = total - running - disabled;
    const ethernet = filteredInterfaces.filter((item) =>
      String(item.type || "")
        .toLowerCase()
        .includes("ether"),
    ).length;

    return {
      totalRouters,
      total,
      running,
      disabled,
      idle,
      ethernet,
    };
  }, [routerInterfaces.length, filteredInterfaces]);

  const initiateToggle = (
    routerId: RouterId,
    name: string,
    disabled: boolean,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    setPendingAction({ routerId, name, disabled });
    setConfirmOpen(true);
  };

  const handleConfirmToggle = async () => {
    if (!pendingAction) return;

    const { routerId, name, disabled } = pendingAction;
    const newState = disabled ? "enable" : "disable";

    setConfirmLoading(true);

    try {
      const res: any = await fetchApp(
        `/interface/state?router_id=${routerId}`,
        {
          method: "POST",
          body: JSON.stringify({ name, state: newState }),
        },
      );

      if (res?.error) {
        showToast(res.error, "error");
      } else {
        const actionText = newState === "enable" ? "enabled" : "disabled";
        showToast(`Interface '${name}' ${actionText} successfully`, "success");
        await fetchInterfaces();
      }
    } catch (err: any) {
      showToast(err?.message || "Failed to update interface state", "error");
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setPendingAction(null);
    }
  };

  const handleEthernetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ethernetForm.name.trim()) {
      showToast("Ethernet name is required", "error");
      return;
    }

    try {
      setEthernetLoading(true);

      const res: any = await fetchMikrotik("/interface/ethernet", {
        method: "POST",
        body: JSON.stringify(ethernetForm),
      });

      if (res?.error) {
        showToast(res.error, "error");
        return;
      }

      showToast("Ethernet interface added successfully", "success");
      await fetchInterfaces();
      closeModal();
    } catch (err: any) {
      showToast(err?.message || "Failed to create Ethernet interface", "error");
    } finally {
      setEthernetLoading(false);
    }
  };

  const handleVlanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!vlanForm.name.trim() || !vlanForm.interface.trim()) {
      showToast("VLAN name and base interface are required", "error");
      return;
    }

    try {
      setVlanLoading(true);

      const res: any = await fetchMikrotik("/interface/vlan", {
        method: "POST",
        body: JSON.stringify(vlanForm),
      });

      if (res?.error) {
        showToast(res.error, "error");
        return;
      }

      showToast("VLAN interface added successfully", "success");
      await fetchInterfaces();
      closeModal();
    } catch (err: any) {
      showToast(err?.message || "Failed to create VLAN interface", "error");
    } finally {
      setVlanLoading(false);
    }
  };

  const handleIPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!ipForm.address.trim() || !ipForm.interface.trim()) {
      showToast("IP address and interface are required", "error");
      return;
    }

    try {
      setIpLoading(true);

      const res: any = await fetchMikrotik("/interface/attach-address", {
        method: "POST",
        body: JSON.stringify(ipForm),
      });

      if (res?.error) {
        showToast(res.error, "error");
        return;
      }

      showToast("IP address attached successfully", "success");
      await fetchInterfaces();
      closeModal();
    } catch (err: any) {
      showToast(err?.message || "Failed to add IP address", "error");
    } finally {
      setIpLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedRouterId("all");
  };

  if (loading && routerInterfaces.length === 0) {
    return <LoadingState />;
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  MikroTik Interface Management
                </div>

                <h1 className="mt-3 text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                  Network Interfaces
                </h1>

                <p className="mt-1 max-w-2xl text-[13px] font-medium text-slate-500 sm:text-[13.5px]">
                  Monitor, filter, and manage Ethernet, VLAN, and IP interface
                  assignments across your routers.
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={fetchInterfaces}
                  disabled={loading}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {loading ? "Refreshing…" : "Refresh"}
                </button>

                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAddMenu((prev) => !prev)}
                    className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                  >
                    <Plus className="h-4 w-4" />
                    Add New
                  </button>

                  {showAddMenu && (
                    <>
                      <button
                        type="button"
                        aria-label="Close add menu"
                        className="fixed inset-0 z-20 cursor-default"
                        onClick={() => setShowAddMenu(false)}
                      />

                      <div className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 p-2 shadow-2xl shadow-slate-400/30">
                        <div className="px-3 py-2 text-[10.5px] font-black uppercase tracking-widest text-slate-400">
                          Create Interface
                        </div>

                        <button
                          type="button"
                          onClick={() => openForm("ethernet")}
                          className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-[13px] font-bold text-slate-700 hover:bg-emerald-50 hover:text-emerald-800"
                        >
                          <EthernetPort className="h-4 w-4 text-emerald-600" />
                          Ethernet Interface
                        </button>

                        <button
                          type="button"
                          onClick={() => openForm("vlan")}
                          className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-[13px] font-bold text-slate-700 hover:bg-sky-50 hover:text-sky-800"
                        >
                          <Settings className="h-4 w-4 text-sky-600" />
                          VLAN Interface
                        </button>

                        <div className="my-1 h-px bg-slate-200" />

                        <button
                          type="button"
                          onClick={() => openForm("ip")}
                          className="flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-[13px] font-bold text-slate-700 hover:bg-amber-50 hover:text-amber-800"
                        >
                          <Server className="h-4 w-4 text-amber-600" />
                          Attach IP Address
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading && routerInterfaces.length > 0 && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing network interfaces…
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Routers"
            value={stats.totalRouters}
            sub="Routers checked"
            icon={<Server className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Interfaces"
            value={stats.total}
            sub="Filtered interface records"
            icon={<Network className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Running"
            value={stats.running}
            sub="Currently active"
            icon={<Activity className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Idle"
            value={stats.idle}
            sub="Enabled but not running"
            icon={<Gauge className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Disabled"
            value={stats.disabled}
            sub="Administratively off"
            icon={<Power className="h-5 w-5" />}
            tone={stats.disabled > 0 ? "rose" : "slate"}
          />
        </div>

        {/* Main Panel */}
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                <Wifi className="h-5 w-5" />
              </div>

              <div>
                <p className="text-[14px] font-black text-slate-900">
                  Interface List
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  Showing {filteredInterfaces.length} of {allInterfaces.length}{" "}
                  interfaces
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:items-center">
              <div className="relative w-full lg:w-80">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name, router, type, comment..."
                  className={`${inputCls} pl-10`}
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-xl p-1.5 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="relative w-full lg:w-72">
                <Filter className="pointer-events-none absolute left-3.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-slate-400" />

                <select
                  value={selectedRouterId}
                  onChange={(e) => setSelectedRouterId(e.target.value)}
                  className={`${inputCls} cursor-pointer appearance-none pl-10 pr-9`}
                >
                  <option value="all">All Routers</option>

                  {routerInterfaces.map((router: any) => (
                    <option
                      key={String(router.router_id)}
                      value={String(router.router_id)}
                    >
                      {router.router_name}
                    </option>
                  ))}
                </select>

                <svg
                  className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {(search || selectedRouterId !== "all") && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left text-[13px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-3.5 font-black">Interface</th>
                  <th className="px-5 py-3.5 font-black">Router</th>
                  <th className="px-5 py-3.5 font-black">Type</th>
                  <th className="px-5 py-3.5 font-black">Status</th>
                  <th className="px-5 py-3.5 text-right font-black">Traffic</th>
                  <th className="px-5 py-3.5 font-black">Last Link Up</th>
                  <th className="px-5 py-3.5 text-right font-black">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, index) => (
                    <TableSkeleton key={index} />
                  ))
                ) : filteredInterfaces.length > 0 ? (
                  filteredInterfaces.map((intf, idx) => {
                    const disabled = getInterfaceDisabled(intf);

                    return (
                      <tr
                        key={`${intf.routerId}-${intf.name}-${idx}`}
                        onClick={() =>
                          navigate(
                            `/interfaces/${intf.routerId}/${encodeURIComponent(
                              intf.name,
                            )}`,
                          )
                        }
                        className="cursor-pointer transition-colors hover:bg-emerald-50/60"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                              <EthernetPort className="h-4 w-4" />
                            </div>

                            <div className="min-w-0">
                              <p className="truncate text-[13px] font-black text-slate-900">
                                {intf.name}
                              </p>

                              <p
                                className="mt-0.5 max-w-[260px] truncate text-[11.5px] font-medium text-slate-500"
                                title={intf.comment || ""}
                              >
                                {intf.comment || "No comment"}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                            <Server className="h-3 w-3" />
                            {intf.routerName}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <TypeBadge type={intf.type} />
                        </td>

                        <td className="px-5 py-4">
                          <StatusBadge intf={intf} />
                        </td>

                        <td className="px-5 py-4 text-right">
                          <div className="flex flex-col items-end gap-1">
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-black text-emerald-700">
                              <ArrowUpDown className="h-3 w-3 rotate-180" />
                              RX {formatBytes((intf as any).rx_byte)}
                            </div>

                            <div className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-black text-sky-700">
                              <ArrowUpDown className="h-3 w-3" />
                              TX {formatBytes((intf as any).tx_byte)}
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                          {(intf as any).last_link_up_time || "—"}
                        </td>

                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={(e) =>
                              initiateToggle(
                                intf.routerId,
                                intf.name,
                                disabled,
                                e,
                              )
                            }
                            className={`${btnBase} h-9 border px-3 ${
                              disabled
                                ? "border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                                : "border-rose-200 bg-rose-100 text-rose-700 hover:bg-rose-200"
                            }`}
                            title={
                              disabled
                                ? "Enable Interface"
                                : "Disable Interface"
                            }
                          >
                            <Power className="h-3.5 w-3.5" />
                            {disabled ? "Enable" : "Disable"}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                        <Filter className="h-7 w-7 text-slate-500" />
                      </div>

                      <p className="text-[14px] font-black text-slate-800">
                        No interfaces found
                      </p>

                      <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                        No interface records match your current filters.
                      </p>

                      {(search || selectedRouterId !== "all") && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                        >
                          Clear all filters
                        </button>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <MobileSkeleton key={index} />
              ))
            ) : filteredInterfaces.length > 0 ? (
              filteredInterfaces.map((intf, idx) => {
                const disabled = getInterfaceDisabled(intf);

                return (
                  <div
                    key={`${intf.routerId}-${intf.name}-${idx}`}
                    className="px-4 py-4"
                    onClick={() =>
                      navigate(
                        `/interfaces/${intf.routerId}/${encodeURIComponent(
                          intf.name,
                        )}`,
                      )
                    }
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-[14px] font-black text-slate-900">
                          {intf.name}
                        </p>

                        <p className="mt-0.5 truncate text-[11.5px] font-medium text-slate-500">
                          {intf.comment || "No comment"}
                        </p>
                      </div>

                      <StatusBadge intf={intf} />
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <TypeBadge type={intf.type} />

                      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                        <Server className="h-3 w-3" />
                        {intf.routerName}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-700">
                          RX Traffic
                        </p>

                        <p className="mt-1 flex items-center gap-1.5 font-mono text-[12.5px] font-black text-slate-900">
                          <ArrowUpDown className="h-3.5 w-3.5 rotate-180 text-emerald-600" />
                          {formatBytes((intf as any).rx_byte)}
                        </p>
                      </div>

                      <div className="rounded-2xl border border-sky-200 bg-sky-50 px-3 py-3">
                        <p className="text-[10px] font-black uppercase tracking-widest text-sky-700">
                          TX Traffic
                        </p>

                        <p className="mt-1 flex items-center gap-1.5 font-mono text-[12.5px] font-black text-slate-900">
                          <ArrowUpDown className="h-3.5 w-3.5 text-sky-600" />
                          {formatBytes((intf as any).tx_byte)}
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-100 px-3 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Last Link Up
                      </p>

                      <p className="mt-1 flex items-center gap-1.5 text-[12px] font-bold text-slate-700">
                        <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
                        {(intf as any).last_link_up_time || "—"}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={(e) =>
                        initiateToggle(intf.routerId, intf.name, disabled, e)
                      }
                      className={`${btnBase} mt-4 h-10 w-full border px-3 ${
                        disabled
                          ? "border-emerald-200 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "border-rose-200 bg-rose-100 text-rose-700 hover:bg-rose-200"
                      }`}
                    >
                      <Power className="h-4 w-4" />
                      {disabled ? "Enable Interface" : "Disable Interface"}
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-16 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <Filter className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No interfaces found
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  No interface records match your current filters.
                </p>

                {(search || selectedRouterId !== "all") && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mt-4 text-[13px] font-black text-emerald-700 hover:text-emerald-800"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2 border-t border-slate-200 bg-slate-100/80 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <p className="text-[12px] font-medium text-slate-500">
              Showing{" "}
              <span className="font-black text-slate-800">
                {filteredInterfaces.length}
              </span>{" "}
              of{" "}
              <span className="font-black text-slate-800">
                {allInterfaces.length}
              </span>{" "}
              interfaces
            </p>

            {(search || selectedRouterId !== "all") && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-left text-[12px] font-black text-emerald-700 hover:text-emerald-800 sm:text-right"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      <InterfaceFormModal
        isOpen={isModalOpen}
        activeForm={activeForm}
        onClose={closeModal}
        ethernetForm={ethernetForm}
        setEthernetForm={setEthernetForm}
        vlanForm={vlanForm}
        setVlanForm={setVlanForm}
        ipForm={ipForm}
        setIpForm={setIpForm}
        interfaces={allInterfaces}
        ethernetLoading={ethernetLoading}
        vlanLoading={vlanLoading}
        ipLoading={ipLoading}
        handleEthernetSubmit={handleEthernetSubmit}
        handleVlanSubmit={handleVlanSubmit}
        handleIPSubmit={handleIPSubmit}
      />

      <ConfirmDialog
        open={confirmOpen}
        title={
          pendingAction?.disabled ? "Enable Interface" : "Disable Interface"
        }
        description={`Are you sure you want to ${
          pendingAction?.disabled ? "enable" : "disable"
        } the interface "${pendingAction?.name}"? ${
          !pendingAction?.disabled
            ? "Disabling this interface might interrupt active connections."
            : ""
        }`}
        confirmLabel={pendingAction?.disabled ? "Enable" : "Disable"}
        variant={pendingAction?.disabled ? "default" : "danger"}
        loading={confirmLoading}
        onCancel={() => {
          if (!confirmLoading) {
            setConfirmOpen(false);
            setPendingAction(null);
          }
        }}
        onConfirm={handleConfirmToggle}
      />
    </div>
  );
};

export default InterfacesPage;
